import { OverlayRef } from '@angular/cdk/overlay';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import {
    CommonMessages,
    DeviceFingerPrint,
    DeviceFingerprintService,
    EventsService,
    LoginType,
    SimpleEvent,
    UserEvent,
    UserLoginEvent,
    UserService,
    VanillaEventNames,
    trackByItem,
} from '@frontend/vanilla/core';
import { LhHeaderBarComponent } from '@frontend/vanilla/features/header-bar';
import { ValidationHelperService } from '@frontend/vanilla/shared/forms';
import { ImageComponent } from '@frontend/vanilla/shared/image';
import { LoginConfig } from '@frontend/vanilla/shared/login';
import { Subject, merge } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { LoginContentService } from '../login-content.service';
import { LoginFailedEvent } from '../login.models';
import { LoginService } from '../login.service';
import { BetstationLoginTrackingService } from './betstation-login-tracking.service';
import { CARD_NUMBER } from './betstation-login.models';
import { BetstationLoginService } from './betstation-login.service';

@Component({
    imports: [LhHeaderBarComponent, ReactiveFormsModule, ImageComponent],
    selector: 'vn-betstation-login-card-pin',
    templateUrl: 'betstation-login-card-pin.html',
})
export class BetstationLoginCardPinComponent implements OnInit, OnDestroy {
    formGroup: UntypedFormGroup;
    errorMessage: string;
    readonly digits = [1, 2, 3, 4, 5, 6, 7, 8, 9, -1, 0];
    readonly trackByItem = trackByItem;

    private deviceFingerPrint: DeviceFingerPrint;
    private readonly destroySubject = new Subject<any>();

    readonly loginConfig = inject(LoginConfig);
    readonly loginContentService = inject(LoginContentService);
    readonly commonMessages = inject(CommonMessages);
    private readonly loginService = inject(LoginService);
    private readonly betstationLoginService = inject(BetstationLoginService);
    private readonly deviceFingerprintService = inject(DeviceFingerprintService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly user = inject(UserService);
    private readonly overlayRef = inject(OverlayRef);
    private readonly validation = inject(ValidationHelperService);
    private readonly trackingService = inject(BetstationLoginTrackingService);
    private readonly eventsService = inject(EventsService);
    private readonly cardNumber = inject(CARD_NUMBER);

    ngOnInit() {
        this.trackingService.trackLoginPinShown();

        this.deviceFingerPrint = this.deviceFingerprintService.get();

        this.formGroup = this.formBuilder.group({
            connectCardNumber: this.formBuilder.control(this.cardNumber, [Validators.required]),
            pin: this.formBuilder.control('', [...this.validation.createValidators('pin')]),
        });

        merge(
            this.user.events.pipe(filter((e: UserEvent) => e instanceof UserLoginEvent)),
            this.loginService.onLoginFailed.pipe(
                filter((e: LoginFailedEvent) => !e.reason?.errorValues?.some((x: any) => x.key === 'REM_INVALID_PSWD_LOGIN_ATTEMPTS_TO_PSWD_BLOCK')),
            ),
            this.eventsService.events.pipe(filter((event: SimpleEvent) => event.eventName === VanillaEventNames.GridConnectLogin)),
        )
            .pipe(takeUntil(this.destroySubject))
            .subscribe(() => {
                this.close();
            });

        this.betstationLoginService.onIncorrectPin.subscribe((message: string) => {
            this.errorMessage = message;
        });
    }

    ngOnDestroy() {
        this.destroySubject.next(null);
    }

    digitClick(digit: number) {
        const newValue: string = this.formGroup.controls.pin?.value + digit;

        if (digit < 0) {
            this.formGroup.controls.pin?.setValue('');
        } else {
            if (newValue.length > 4) return;
            this.formGroup.controls.pin?.setValue(newValue);
        }
    }

    login(evt: Event) {
        evt.preventDefault();
        this.formGroup.disable();
        const model = this.formGroup.value;
        model.fingerprint = this.deviceFingerPrint;
        model.loginType = LoginType.ConnectCard;
        model.loyaltyLoginEnabled = this.loginConfig.loyaltyLoginEnabled;

        this.betstationLoginService.gridConnectLogin(model, () => {
            this.formGroup.enable();
            this.formGroup.controls.pin?.setValue('');
            this.formGroup.controls.pin?.markAsUntouched();
        });
    }

    close() {
        this.overlayRef.detach();
    }
}
